import {
  Box,
  HALF_PI,
  StateNode,
  Vec,
  isAccelKey,
  kickoutOccludedShapes,
  rotateSelectionHandle
} from "@tldraw/editor";
import { getCropBox, getDefaultCrop, getUncroppedSize } from "../../../../../shapes/shared/crop.mjs";
import { CursorTypeMap } from "../../PointingResizeHandle.mjs";
class Cropping extends StateNode {
  static id = "cropping";
  static trackPerformance = true;
  info = {};
  markId = "";
  snapshot = {};
  onEnter(info) {
    this.info = info;
    if (typeof info.onInteractionEnd === "string") {
      this.parent.setCurrentToolIdMask(info.onInteractionEnd);
    }
    this.markId = this.editor.markHistoryStoppingPoint("cropping");
    this.snapshot = this.createSnapshot();
    this.updateShapes();
  }
  onPointerMove() {
    this.updateShapes();
  }
  onKeyDown() {
    this.updateShapes();
  }
  onKeyUp() {
    this.updateShapes();
  }
  onPointerUp() {
    this.complete();
  }
  onComplete() {
    this.complete();
  }
  onCancel() {
    this.cancel();
  }
  onExit() {
    this.parent.setCurrentToolIdMask(void 0);
    this.editor.snaps.clearIndicators();
  }
  updateCursor() {
    const selectedShape = this.editor.getSelectedShapes()[0];
    if (!selectedShape) return;
    const cursorType = CursorTypeMap[this.info.handle];
    this.editor.setCursor({ type: cursorType, rotation: this.editor.getSelectionRotation() });
  }
  updateShapes() {
    const { editor } = this;
    const { shape, cursorHandleOffset, initialSelectionPageBounds, selectionRotation } = this.snapshot;
    if (!shape) return;
    const util = editor.getShapeUtil(shape.type);
    if (!util) return;
    const shiftKey = editor.inputs.getShiftKey();
    const altKey = editor.inputs.getAltKey();
    const isHoldingAccel = isAccelKey(editor.inputs);
    const currentPagePoint = editor.inputs.getCurrentPagePoint().clone().sub(cursorHandleOffset);
    const originPagePoint = editor.inputs.getOriginPagePoint().clone().sub(cursorHandleOffset);
    if (editor.getInstanceState().isGridMode && !isHoldingAccel) {
      const { gridSize } = editor.getDocumentSettings();
      currentPagePoint.snapToGrid(gridSize);
    }
    editor.snaps.clearIndicators();
    const shouldSnap = editor.user.getIsSnapMode() ? !isHoldingAccel : isHoldingAccel;
    let didSnap = false;
    if (shouldSnap && initialSelectionPageBounds && selectionRotation % HALF_PI === 0) {
      const { nudge } = editor.snaps.shapeBounds.snapResizeShapes({
        dragDelta: Vec.Sub(currentPagePoint, originPagePoint),
        initialSelectionPageBounds,
        handle: rotateSelectionHandle(this.info.handle, selectionRotation),
        isAspectRatioLocked: shiftKey,
        isResizingFromCenter: altKey
      });
      currentPagePoint.add(nudge);
      didSnap = true;
    }
    const change = currentPagePoint.clone().sub(originPagePoint).rot(-shape.rotation);
    const crop = shape.props.crop ?? getDefaultCrop();
    const uncroppedSize = getUncroppedSize(shape.props, crop);
    const cropFn = util.onCrop?.bind(util) ?? getCropBox;
    const partial = cropFn(shape, {
      handle: this.info.handle,
      change,
      crop,
      uncroppedSize,
      initialShape: this.snapshot.shape,
      aspectRatioLocked: shiftKey,
      isResizingFromCenter: altKey
    });
    if (partial) {
      editor.updateShapes([
        {
          id: shape.id,
          type: shape.type,
          ...partial
        }
      ]);
    }
    if (didSnap && initialSelectionPageBounds) {
      this.reconcileSnapIndicators(
        initialSelectionPageBounds,
        Vec.Sub(currentPagePoint, originPagePoint),
        rotateSelectionHandle(this.info.handle, selectionRotation),
        shiftKey,
        altKey
      );
    }
    if (partial) this.updateCursor();
  }
  reconcileSnapIndicators(initialSelectionPageBounds, snappedDelta, handle, isAspectRatioLocked, isResizingFromCenter) {
    const { editor } = this;
    if (!editor.snaps.getIndicators().length) return;
    const actual = editor.getShapePageBounds(this.snapshot.shape.id);
    if (!actual) return;
    const { box } = Box.Resize(
      initialSelectionPageBounds,
      handle,
      isResizingFromCenter ? snappedDelta.x * 2 : snappedDelta.x,
      isResizingFromCenter ? snappedDelta.y * 2 : snappedDelta.y,
      isAspectRatioLocked
    );
    if (isResizingFromCenter) box.center = initialSelectionPageBounds.center;
    const EPSILON = 1;
    if (Math.abs(actual.minX - box.minX) > EPSILON || Math.abs(actual.maxX - box.maxX) > EPSILON || Math.abs(actual.minY - box.minY) > EPSILON || Math.abs(actual.maxY - box.maxY) > EPSILON) {
      editor.snaps.clearIndicators();
    }
  }
  complete() {
    this.updateShapes();
    kickoutOccludedShapes(this.editor, [this.snapshot.shape.id]);
    const { onInteractionEnd } = this.info;
    if (onInteractionEnd) {
      if (typeof onInteractionEnd === "string") {
        this.editor.setCurrentTool(onInteractionEnd, this.info);
      } else {
        onInteractionEnd();
      }
    } else {
      this.editor.setCroppingShape(null);
      this.editor.setCurrentTool("select.idle");
    }
  }
  cancel() {
    this.editor.bailToMark(this.markId);
    const { onInteractionEnd } = this.info;
    if (onInteractionEnd) {
      if (typeof onInteractionEnd === "string") {
        this.editor.setCurrentTool(onInteractionEnd, this.info);
      } else {
        onInteractionEnd();
      }
    } else {
      this.editor.setCroppingShape(null);
      this.editor.setCurrentTool("select.idle");
    }
  }
  createSnapshot() {
    const selectionRotation = this.editor.getSelectionRotation();
    const originPagePoint = this.editor.inputs.getOriginPagePoint();
    const shape = this.editor.getOnlySelectedShape();
    const selectionBounds = this.editor.getSelectionRotatedPageBounds();
    const dragHandlePoint = Vec.RotWith(
      selectionBounds.getHandlePoint(this.info.handle),
      selectionBounds.point,
      selectionRotation
    );
    const cursorHandleOffset = Vec.Sub(originPagePoint, dragHandlePoint);
    const initialSelectionPageBounds = this.editor.getSelectionPageBounds();
    return {
      shape,
      cursorHandleOffset,
      initialSelectionPageBounds,
      selectionRotation
    };
  }
}
export {
  Cropping
};
//# sourceMappingURL=Cropping.mjs.map
